import NewReminderFab from '@/common/components/new-reminder-fab/new-reminder-fab';
import RemindersList from '@common/components/reminders/RemindersList';
import { Box } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectCurrentWaChat } from '@/components/team-inbox/redux/TeamInboxSelectors';
import { getWaChatContactDisplayName } from '@/common/utils/display-name-utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

type ReminderScope = 'current' | 'all';

const RemindersSidebar: React.FC = () => {
    const { t, i18n } = useTranslation(namespace);
    const teamInboxCurrentChat = useAppSelector(selectCurrentWaChat);
    const whatsappCurrentChat = useAppSelector((state) => state.WhatsAppReducer.currentChat);
    const isRTL = i18n.dir(i18n.resolvedLanguage || i18n.language) === 'rtl';

    const currentChatId = useMemo(() => {
        const teamInboxId = (teamInboxCurrentChat as any)?._serialized
            || (teamInboxCurrentChat as any)?.id?._serialized
            || (teamInboxCurrentChat as any)?._id;
        const whatsappId = whatsappCurrentChat?.id?._serialized;
        return teamInboxId || whatsappId || '';
    }, [teamInboxCurrentChat, whatsappCurrentChat]);

    const currentChatDisplayName = useMemo(() => {
        const teamInboxName = (teamInboxCurrentChat as any)?.name || (teamInboxCurrentChat as any)?.formattedTitle;
        if (teamInboxName) return teamInboxName;
        return getWaChatContactDisplayName(whatsappCurrentChat, { fallback: '' }) || '';
    }, [teamInboxCurrentChat, whatsappCurrentChat]);

    const [scope, setScope] = useState<ReminderScope>('all');

    useEffect(() => {
        if (!currentChatId && scope === 'current') {
            setScope('all');
        }
    }, [currentChatId, scope]);

    return (
        <div className="h-full w-full flex flex-col bg-repeat overflow-hidden">
            <Box sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                p: 2
            }}>
                <div className={`mb-3 flex items-center gap-2 ${isRTL ? 'justify-end' : 'justify-start'}`}>
                    <Button
                        size="sm"
                        variant={scope === 'current' ? 'secondary' : 'outline'}
                        onClick={() => setScope('current')}
                        disabled={!currentChatId}
                    >
                        {t('teamInbox.reminderCurrentChat', 'Current chat')}
                    </Button>
                    <Button
                        size="sm"
                        variant={scope === 'all' ? 'secondary' : 'outline'}
                        onClick={() => setScope('all')}
                    >
                        {t('teamInbox.reminderAllChats', 'All chats')}
                    </Button>
                </div>

                {scope === 'current' && currentChatDisplayName && (
                    <p className={`text-xs text-muted-foreground mb-2 truncate ${isRTL ? 'text-right' : ''}`}>
                        {currentChatDisplayName}
                    </p>
                )}

                <div className="flex-1 min-h-0 w-full">
                    <RemindersList scope={scope} currentChatId={currentChatId} />
                </div>
                <NewReminderFab
                    scope={scope}
                    currentChatId={currentChatId}
                    currentChatDisplayName={currentChatDisplayName}
                />
            </Box>
        </div>
    );
};

export default RemindersSidebar;
